import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import type { Action } from '@ngrx/store';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { mergeMap, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { LocationBrief } from '@core/locations-types';
import { createEffectFactory } from '@core/ngrx-legacy';

import { LocationsHttpClient } from './locations.http-client';
import { LocationActions } from './locations.store';
import { LocationsBriefResponse } from '../location.types';

@Injectable({ providedIn: 'root' })
export class LocationsEffects {
  private readonly actions$ = inject(Actions);
  private readonly http = inject(LocationsHttpClient);
  private readonly store = inject(Store);

  public effects = createEffectFactory(LocationActions, this.actions$);

  public fetchCollection$ = this.effects(LocationActions.fetchPaginatedCollection, ({ payload }) =>
    this.http
      .fetchPaginatedCollection(payload.scope, payload.params)
      .pipe(map(response => ({ data: response, scope: payload.scope }))),
  );

  public fetchBriefCollection$ = this.effects(LocationActions.fetchBriefCollection, () =>
    this.fetchBriefCollection([], 1).pipe(
      map(response =>
        LocationActions.fetchBriefCollectionSuccess({
          payload: {
            data: response.locations,
          },
        }),
      ),
      catchError(error =>
        of(
          LocationActions.fetchBriefCollectionFailed({
            payload: { error },
          }),
        ),
      ),
    ),
  );

  public fetchOne$ = this.effects(
    LocationActions.fetchOne,
    ({ payload }) =>
      this.http.fetchOne(payload.id).pipe(map(response => ({ data: response.location }))),
    { customMapOperator: mergeMap },
  );

  public create$ = this.effects(LocationActions.createOnePessimistic, ({ payload }) =>
    this.http
      .create({ location: payload.data })
      .pipe(map(response => ({ data: response.location }))),
  );

  public update$ = this.effects(LocationActions.updateOnePessimistic, ({ payload }) =>
    this.http
      .update({ location: payload.data }, payload.id)
      .pipe(map(response => ({ data: response.location }))),
  );

  public archive$ = this.effects(LocationActions.archive, ({ payload }) =>
    this.http.archive(payload.id).pipe(
      map(response => LocationActions.archiveSuccess({ payload: { data: response.location } })),
      catchError(error => of(LocationActions.archiveFailed({ payload: error }))),
    ),
  );

  public retrieve$ = this.effects(LocationActions.retrieve, ({ payload }) =>
    this.http.retrieve(payload.id).pipe(
      map(response => LocationActions.retrieveSuccess({ payload: { data: response.location } })),
      catchError(error => of(LocationActions.retrieveFailed({ payload: error }))),
    ),
  );

  public duplicate$ = this.effects(LocationActions.duplicate, ({ payload }) =>
    this.http.duplicate(payload.id).pipe(
      map(response => LocationActions.duplicateSuccess({ payload: { data: response.location } })),
      catchError(error => of(LocationActions.duplicateFailed({ payload: error }))),
    ),
  );

  public delete$ = this.effects(LocationActions.delete, ({ payload }) =>
    this.http.delete(payload.data).pipe(
      map(response => LocationActions.deleteSuccess({ payload: { data: response.location } })),
      catchError(error => of(LocationActions.deleteFailed({ payload: error }))),
    ),
  );

  public fetchAssignedLocationsToOffer$ = this.effects(
    LocationActions.fetchAssignedLocationsToOffer,
    data =>
      this.http.fetchAssignedLocationsToOffer(data.payload.offerId).pipe(
        map(response =>
          LocationActions.fetchAssignedLocationsToOfferSuccess({
            payload: {
              locations: response.locations,
              locationIds: data.payload.locationIds,
              offerId: data.payload.offerId,
            },
          }),
        ),
        catchError(error =>
          of(
            LocationActions.fetchAssignedLocationsToOfferFailed({
              payload: {
                error,
                data: { locationIds: data.payload.locationIds, offerId: data.payload.offerId },
              },
            }),
          ),
        ),
      ),
  );

  public fetchAssignedLocationsToOfferTemplate$ = this.effects(
    LocationActions.fetchAssignedLocationsToOfferTemplate,
    data =>
      this.http.fetchAssignedLocationsToOfferTemplate(data.payload.offerTemplateId).pipe(
        map(response =>
          LocationActions.fetchAssignedLocationsToOfferTemplateSuccess({
            payload: {
              locations: response.locations,
              locationIds: data.payload.locationIds,
              offerTemplateId: data.payload.offerTemplateId,
            },
          }),
        ),
        catchError(error =>
          of(
            LocationActions.fetchAssignedLocationsToOfferTemplateFailed({
              payload: {
                error,
                data: {
                  locationIds: data.payload.locationIds,
                  offerTemplateId: data.payload.offerTemplateId,
                },
              },
            }),
          ),
        ),
      ),
  );

  public fetchLocationsWithMissingTranslations$ = this.effects(
    LocationActions.fetchWithMissingTranslations,
    ({ payload }) =>
      this.http.fetchWithMissingTranslations(payload.langCode).pipe(
        map(locations =>
          LocationActions.fetchWithMissingTranslationsSuccess({
            payload: {
              locations,
              langCode: payload.langCode,
            },
          }),
        ),
        catchError(error =>
          of(
            LocationActions.fetchWithMissingTranslationsFailed({
              payload: { error },
            }),
          ),
        ),
      ),
  );

  public refreshFromWS$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(ofType(LocationActions.updateFromWS))
      .pipe(
        switchMap(({ payload: { ids: locationIds } }) => [
          ...locationIds.map(id => LocationActions.fetchOne({ payload: { id } })),
        ]),
      ),
  );

  private fetchBriefCollection(
    locations: LocationBrief[],
    page: number,
  ): Observable<LocationsBriefResponse> {
    return this.http.fetchBriefCollection(page).pipe(
      switchMap(response => {
        const fetchedAll = response.meta.limit * response.meta.page > response.meta.totalCount;
        return fetchedAll
          ? of<LocationsBriefResponse>({
              ...response,
              locations: [...locations, ...response.locations],
            })
          : this.fetchBriefCollection(
              [...locations, ...response.locations],
              response.meta.page + 1,
            );
      }),
    );
  }
}
